import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  Button,
  Grid,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import RouteIcon from '@mui/icons-material/Route';
import { useNavigate } from 'react-router-dom';
import DriversInspectionTable from './DriversInspectionTable';
import DriverOperationalLogs from './DriverOperationalLogs';
import DriverActivities from './DriverActivities';

export default function Drivers() {
  const navigate = useNavigate();
  const [driverTab, setDriverTab] = React.useState('activities');

  const actions = [
    {
      value: 'activities',
      label: 'Activities',
      title: 'Driver Activities',
      description: 'Review fuel records, trip logs, and driver check-ins in one place.',
      color: 'primary',
      icon: <PeopleAltIcon sx={{ color: '#001f3f' }} />,
    },
    {
      value: 'pre_trip',
      label: 'Pre-Trip',
      title: 'Pre-Trip Check-In',
      description: 'Select a truck and complete a pre-trip inspection before driving.',
      button: 'Start Pre-Trip',
      color: 'primary',
      icon: <FactCheckIcon sx={{ color: '#1565c0' }} />,
      to: '/drivers/check-in/pre_trip',
    },
    {
      value: 'post_trip',
      label: 'Post-Trip',
      title: 'Post-Trip Check-In',
      description: 'Select a truck and complete a post-trip inspection after returning.',
      button: 'Start Post-Trip',
      color: 'success',
      icon: <AssignmentTurnedInIcon sx={{ color: '#2e7d32' }} />,
      to: '/drivers/check-in/post_trip',
    },
    {
      value: 'defect_report',
      label: 'Defect',
      title: 'Defect Report',
      description: 'Report any defect observed on any truck as a separate driver workflow.',
      button: 'Report Defect',
      color: 'warning',
      icon: <WarningAmberIcon sx={{ color: '#ed6c02' }} />,
      to: '/drivers/check-in/defect_report',
    },
    {
      value: 'fuel',
      label: 'Fuel',
      title: 'Fuel Records',
      description: 'Record fuel added to a vehicle and review or delete your previous fuel entries.',
      color: 'primary',
      icon: <LocalGasStationIcon sx={{ color: '#1565c0' }} />,
    },
    {
      value: 'trip_log',
      label: 'Trip Log',
      title: 'Trip Logs',
      description: 'Record trips for vans, service vehicles, tractors, trailers, forklifts, and skid steers.',
      color: 'primary',
      icon: <RouteIcon sx={{ color: '#1565c0' }} />,
    },
  ];

  const selectedAction = actions.find((action) => action.value === driverTab) || actions[0];

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.75, md: 2 }, minHeight: '100dvh' }}>
      <Card className="portal-page-header"
        sx={{
          mb: 1.25,
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white',
          borderRadius: 2.25,
          boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)',
        }}
      >
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 } }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                backgroundColor: 'rgba(255,255,255,0.22)',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.14)',
              }}
            >
              <PeopleAltIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: 'white' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05 }}>
                Drivers
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                 Inspections, defects, fuel records, and trip logs
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Tabs
          value={driverTab}
          onChange={(_event, value) => setDriverTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #e0e0e0',
            bgcolor: 'white',
            '& .MuiTabs-flexContainer': { minWidth: 'max-content' },
            '& .MuiTab-root': {
              fontWeight: 900,
              minHeight: { xs: 58, sm: 62 },
              minWidth: { xs: 92, sm: 120 },
              px: { xs: 1.25, sm: 2 },
              fontSize: { xs: '0.75rem', sm: '0.82rem' },
            },
            '& .Mui-selected': { color: '#001f3f' },
            '& .MuiTabs-indicator': { bgcolor: '#00ff97', height: 3 },
          }}
        >
          {actions.map((action) => (
            <Tab key={action.value} value={action.value} label={action.label} icon={React.cloneElement(action.icon, { sx: { fontSize: { xs: 20, sm: 22 } } })} iconPosition="start" />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 } }}>
          {driverTab === 'activities' ? (
            <DriverActivities />
          ) : selectedAction.to ? (
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1.75, sm: 2.5 }, height: '100%', borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {selectedAction.icon}
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{selectedAction.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{selectedAction.description}</Typography>
                  <Button fullWidth variant="contained" color={selectedAction.color} onClick={() => navigate(selectedAction.to)} sx={{ borderRadius: 2, minHeight: 48, fontWeight: 900 }}>
                    {selectedAction.button}
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <DriverOperationalLogs type={driverTab === 'fuel' ? 'fuel' : 'trip'} />
          )}
        </Box>
      </Paper>

      {selectedAction.to && <DriversInspectionTable />}
    </Box>
  );
}
